import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Gavel,
  Email,
  Phone,
} from '@mui/icons-material';
import PropTypes from 'prop-types';

const TermsOfService = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeSection, setActiveSection] = useState('section-1');

  // 페이지 진입 시 항상 최상단으로 스크롤
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 스크롤 감지 로직
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      let current = '';

      sections.forEach((section) => {
        const sectionTop = section.offsetTop;
        if (window.scrollY >= sectionTop - 150) {
          current = section.getAttribute('id');
        }
      });

      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 100,
        behavior: 'smooth',
      });
      setActiveSection(id);
    }
  };

  // 공통 컴포넌트: 섹션 타이틀
  const SectionTitle = ({ children }) => (
    <Box sx={{ mb: 3, mt: 6, borderBottom: 1, borderColor: 'divider', pb: 1 }}>
      <Typography variant="h5" component="h2" fontWeight="bold" color="text.primary">
        {children}
      </Typography>
    </Box>
  );

  // 내비게이션 아이템 데이터
  const navItems = [
    { id: 'section-1', label: '1. 목적' },
    { id: 'section-2', label: '2. 용어의 정의' },
    { id: 'section-3', label: '3. 약관의 효력 및 변경' },
    { id: 'section-4', label: '4. 서비스의 제공 및 변경' },
    { id: 'section-5', label: '5. 유료 서비스 및 자동갱신형 구독' },
    { id: 'section-6', label: '6. 이용자의 의무' },
    { id: 'section-7', label: '7. 서비스 이용의 제한 및 계약 해지' },
    { id: 'section-8', label: '8. 면책조항' },
    { id: 'section-9', label: '9. 분쟁 해결 및 관할' },
    { id: 'section-10', label: '10. 문의처' },
  ];

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', py: 5 }}>
      <Container maxWidth="lg">
        {/* 헤더 */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" component="h1" fontWeight="800" gutterBottom>
            서비스 이용약관
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            주식회사 가람마인드 (이하 “회사”)
          </Typography>
          <Chip
            icon={<Gavel />}
            label="말랑포켓 서비스 이용에 관한 조건과 절차를 안내합니다."
            color="primary"
            variant="outlined"
            sx={{ mt: 2, px: 1, py: 2.5, borderRadius: 8 }}
          />
        </Box>

        <Grid container spacing={4}>
          {/* 사이드 내비게이션 (데스크탑: Sticky, 모바일: 상단 배치) */}
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                position: isMobile ? 'static' : 'sticky',
                top: 100,
                bgcolor: 'white',
                border: 1,
                borderColor: 'divider'
              }}
            >
              <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ px: 2, mb: 1, display: 'block' }}>
                목차
              </Typography>
              <List component="nav" dense>
                {navItems.map((item) => (
                  <ListItemButton
                    key={item.id}
                    selected={activeSection === item.id}
                    onClick={() => scrollToSection(item.id)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      '&.Mui-selected': {
                        bgcolor: 'primary.light',
                        color: 'primary.dark',
                        borderLeft: '4px solid',
                        borderColor: 'primary.main',
                        '&:hover': { bgcolor: 'primary.light' },
                      },
                    }}
                  >
                    <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* 본문 콘텐츠 */}
          <Grid item xs={12} md={9}>
            <Paper elevation={1} sx={{ p: { xs: 3, md: 5 }, borderRadius: 2 }}>

              <Typography paragraph color="text.secondary">
                말랑포켓 및 관련 서비스(이하 “서비스”)를 이용해 주셔서 감사합니다. 본 약관은 회사가 제공하는 서비스의 이용조건 및 절차, 회사와 이용자의 권리·의무 및 책임사항을 규정합니다.
              </Typography>

              {/* 제1조 */}
              <section id="section-1">
                <SectionTitle>1. 목적</SectionTitle>
                <Typography paragraph>
                  본 약관은 이용자가 회사가 제공하는 말랑포켓 앱 및 관련 제반 서비스를 이용함에 있어 회사와 이용자 간의 권리, 의무 및 책임사항, 기타 필요한 사항을 규정함을 목적으로 합니다.
                </Typography>
              </section>

              {/* 제2조 */}
              <section id="section-2">
                <SectionTitle>2. 용어의 정의</SectionTitle>
                <List dense>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
                    “서비스”란 회사가 모바일 애플리케이션 및 관련 웹을 통해 제공하는 감정 기록, AI 대화, 검사, 리포트 등 일체의 기능을 말합니다.
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3, mt: 1 }}>
                    “이용자”란 본 약관에 따라 회사가 제공하는 서비스를 이용하는 회원 및 비회원을 말합니다.
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3, mt: 1 }}>
                    “유료 서비스”란 회사가 유료로 제공하는 자동갱신형 구독 상품(“말랑패스” 등)을 말하며, 상품명·구독 기간·가격 등 구체적인 내용은 앱 내 결제 화면 및 각 스토어(App Store, Google Play)의 상품 정보에 안내된 바에 따릅니다.
                  </ListItem>
                </List>
              </section>

              {/* 제3조 */}
              <section id="section-3">
                <SectionTitle>3. 약관의 효력 및 변경</SectionTitle>
                <List dense>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
                    본 약관은 서비스 화면에 게시하거나 기타의 방법으로 이용자에게 공지함으로써 효력이 발생합니다.
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3, mt: 1 }}>
                    회사는 관련 법령을 위배하지 않는 범위에서 본 약관을 개정할 수 있으며, 개정 시 적용일자 및 개정 사유를 명시하여 최소 7일 전(이용자에게 불리한 변경의 경우 30일 전)부터 공지합니다.
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3, mt: 1 }}>
                    이용자가 개정 약관의 적용에 동의하지 않는 경우, 이용자는 서비스 이용을 중단하고 탈퇴할 수 있습니다. 공지 후에도 이용자가 서비스를 계속 이용하는 경우 개정 약관에 동의한 것으로 봅니다.
                  </ListItem>
                </List>
              </section>

              {/* 제4조 */}
              <section id="section-4">
                <SectionTitle>4. 서비스의 제공 및 변경</SectionTitle>
                <List dense>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
                    회사는 감정 기록, AI 챗봇 대화, 감정 통계, 검사, 종합 리포트 등의 서비스를 제공합니다.
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3, mt: 1 }}>
                    회사는 운영상, 기술상의 필요에 따라 제공하는 서비스의 전부 또는 일부를 변경할 수 있으며, 이 경우 변경 내용 및 시행일을 사전에 공지합니다.
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3, mt: 1 }}>
                    회사는 서비스 점검, 설비의 보수, 교체 등 불가피한 경우 서비스 제공을 일시 중단할 수 있으며, 이 경우 가능한 사전에 공지합니다.
                  </ListItem>
                </List>
              </section>

              {/* 제5조 */}
              <section id="section-5">
                <SectionTitle>5. 유료 서비스 및 자동갱신형 구독</SectionTitle>
                <List dense>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
                    회사는 서비스 내 “말랑패스” 등 유료 구독 상품을 제공할 수 있으며, 상품의 명칭, 구독 기간(예: 1개월), 결제 금액 및 결제 주기는 앱 내 구독 화면에 표시됩니다.
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3, mt: 1 }}>
                    유료 서비스는 별도의 해지 신청이 없는 한 각 구독 기간이 종료되는 시점에 동일한 조건으로 자동으로 갱신되며, 결제는 이용자가 가입한 스토어(Apple App Store 또는 Google Play)의 결제 수단을 통해 각 구독 주기 시작 시점에 자동으로 청구됩니다.
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3, mt: 1 }}>
                    이용자는 현재 구독 기간이 종료되기 최소 24시간 전까지 아래의 방법으로 자동갱신을 해지할 수 있으며, 해지하더라도 이미 결제된 구독 기간 동안에는 유료 서비스를 계속 이용할 수 있습니다.
                    <List dense sx={{ mt: 1 }}>
                      <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
                        iOS: 기기의 [설정] &gt; [Apple ID] &gt; [구독] 메뉴에서 해지
                      </ListItem>
                      <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
                        Android: Google Play 스토어 앱 &gt; [결제 및 구독] &gt; [구독] 메뉴에서 해지
                      </ListItem>
                    </List>
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3, mt: 1 }}>
                    결제 및 환불은 이용자가 가입한 스토어(Apple 또는 Google)의 정책이 우선 적용되며, 각 스토어의 환불 정책에 따라 처리됩니다.
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3, mt: 1 }}>
                    회사는 프로모션 코드, 할인 혜택 등을 제공할 수 있으며, 각 혜택의 적용 조건 및 기간은 별도로 안내합니다.
                  </ListItem>
                </List>
              </section>

              {/* 제6조 */}
              <section id="section-6">
                <SectionTitle>6. 이용자의 의무</SectionTitle>
                <List dense>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
                    이용자는 관련 법령, 본 약관의 규정, 이용안내 및 서비스와 관련하여 공지한 주의사항 등을 준수해야 하며, 기타 회사의 업무에 방해되는 행위를 해서는 안 됩니다.
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3, mt: 1 }}>
                    이용자는 서비스를 이용하여 얻은 정보를 회사의 사전 승낙 없이 복제, 유통, 상업적으로 이용할 수 없습니다.
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3, mt: 1 }}>
                    이용자는 타인의 계정을 도용하거나 허위 정보를 등록해서는 안 됩니다.
                  </ListItem>
                </List>
              </section>

              {/* 제7조 */}
              <section id="section-7">
                <SectionTitle>7. 서비스 이용의 제한 및 계약 해지</SectionTitle>
                <List dense>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
                    회사는 이용자가 본 약관을 위반하거나 서비스의 정상적인 운영을 방해한 경우, 사전 통지 후 서비스 이용을 제한하거나 계약을 해지할 수 있습니다.
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3, mt: 1 }}>
                    이용자는 언제든지 서비스 내 설정 화면을 통해 이용계약 해지(회원 탈퇴)를 신청할 수 있으며, 회사는 관련 법령 및 개인정보 처리방침에 따라 이를 처리합니다.
                  </ListItem>
                </List>
              </section>

              {/* 제8조 */}
              <section id="section-8">
                <SectionTitle>8. 면책조항</SectionTitle>
                <List dense>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
                    본 서비스 및 AI 상담 에이전트가 제공하는 정보, 피드백, 해석, 조언 등은 이용자의 자기 이해 및 정서 관리를 돕기 위한 보조적 도구이며, 의료행위·정신건강의학과 진료·심리치료·공식 심리상담을 대체하지 않습니다.
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3, mt: 1 }}>
                    회사는 천재지변, 불가항력, 이용자의 귀책사유 또는 제3자(스토어 사업자 포함)의 사정으로 인하여 발생한 서비스 장애에 대해서는 관련 법령이 허용하는 범위 내에서 책임을 제한합니다.
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3, mt: 1 }}>
                    회사는 이용자가 서비스를 이용하여 기대하는 효과를 얻지 못한 것에 대하여 책임을 지지 않으며, 서비스에 게재된 정보의 신뢰도, 정확성 등에 대해서는 회사의 고의 또는 중대한 과실이 없는 한 책임을 지지 않습니다.
                  </ListItem>
                </List>
              </section>

              {/* 제9조 */}
              <section id="section-9">
                <SectionTitle>9. 분쟁 해결 및 관할</SectionTitle>
                <List dense>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
                    본 약관은 대한민국 법령에 따라 해석되고 적용됩니다.
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3, mt: 1 }}>
                    서비스 이용과 관련하여 회사와 이용자 간 분쟁이 발생한 경우, 상호 협의하여 원만히 해결하도록 노력하며, 협의가 이루어지지 않는 경우 관련 법령이 정한 절차에 따릅니다.
                  </ListItem>
                </List>
              </section>

               {/* 10. 문의처 */}
              <section id="section-10">
                <SectionTitle>10. 문의처</SectionTitle>
                <Paper variant="outlined" sx={{ p: 3, bgcolor: 'grey.50' }}>
                  <Typography paragraph color="text.secondary">
                    이용자는 서비스 이용과 관련한 문의사항을 아래 연락처 또는 서비스 내 [설정 &gt; 문의하기]를 통해 접수할 수 있습니다.
                  </Typography>
                 <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2">책임자 / 직위</Typography>
                       <Typography variant="body2">홍수희 / 사원  </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2">소속</Typography>
                       <Typography variant="body2">주식회사 가람마인드  </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Email fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                         <Typography variant="body2">hong9849@garam-mind.com  </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Phone fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                         <Typography variant="body2">010-9424-3355  </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
                <Typography variant="caption" display="block" align="right" sx={{ mt: 2, color: 'text.disabled' }}>
                  공고일자: 2026년 9월 4일 / 시행일자: 2026년 9월 4일
                </Typography>
              </section>

            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

TermsOfService.propTypes = {
  children: PropTypes.any,
};

export default TermsOfService;
